/* eslint-disable multiline-ternary */
import { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import Datepicker from '@/components/time/Datepicker';
import { Toaster, toast } from 'sonner';
import { type ReportConfig } from '@/types/ReportConfig';
import DropdownFull from '@/components/dropdowns/DropdownFull';

import {
  getAllReportConfigs,
  getAllReportScheduleConfigs,
  saveNewReportScheduleConfig,
  updateReportScheduleConfig,
  deleteReportScheduleConfig,
  getFranchiseUsers,
} from '@/utils/api';
import { type ReportScheduleConfig } from '@/types/ReportScheduleConfig';
import TimeSelect from '@/components/time/TimeSelect';
import { type FranchiseUser } from '@/types/FranchiseUser';
import DropdownFilter from '@/components/dropdowns/DropdownFilter';
import { useAuth } from '@/hooks/useAuth';
import { Roles } from '@/types/Roles';
import { utc } from 'moment';

export default function ReportSchedules(): JSX.Element {
  const {
    selectedFranchise,
    selectedDates,
    handleSelectedDates,
    profile,
  } = useData();
  const { user } = useAuth();
  const [selectedReportConfigId, setSelectedReportConfigId] =
    useState<string>('');

  const [reportConfigOptions, setReportConfigOptions] = useState<
    ReportConfig[]
  >([]);
  const [selectedReportConfig, setSelectedReportConfig] = useState({
    displayName: '-- Select a Report Config --',
    reportId: '',
  });
  const [displayedUsers, setDisplayedUsers] =  useState<
    FranchiseUser[] | Array<{ userId: string; email: string }>
  >([]);
  const [selectedReportScheduleConfigId, setSelectedReportScheduleConfigId] =
    useState<string>('');

  const [reportScheduleConfigOptions, setReportScheduleConfigOptions] =
    useState<ReportScheduleConfig[]>([]);

  const [selectedReportScheduleConfig, setSelecteReportScheduleConfig] =
    useState({
      displayName: '-- Create New Schedule --',
      reportScheduleId: '',
    });

  const [selectedFranchiseUsers, setSelectedFranchiseUsers] = useState<
    Array<{ id: string; value: string }>
  >([]);

  const [franchiseUsers, setFranchiseUsers] = useState<
    FranchiseUser[] | Array<{ userId: string; email: string }>
  >([]);

  // frequency options (days, weeks, months)
  const frequencies = [
    { label: 'Days', value: 'd' },
    { label: 'Weeks', value: 'w' },
    { label: 'Months', value: 'M' },
    { label: 'Quarter', value: 'Q' },
    { label: 'Years', value: 'Y' }
  ];
  const [selectedFrequency, setSelectedFrequency] = useState({
    label: '-',
    value: '',
  });

  const [
    selectedReportScheduleConfigName,
    setselectedReportScheduleConfigName,
  ] = useState<string>('');

  const [reportFrequency, setReportFrequency] = useState<string>('');

  const [selectedTime, setSelectedTime] = useState<string>('00:00');

  const fetchReportAndScheduleConfigs = async (): Promise<void> => {
    if (selectedFranchise != null) {
      const noneConfig: ReportConfig[] = [
        {
          displayName: '-- Select a Report Config --',
          reportId: '',
          locations: [],
          detectionType: 'DT',
          timeSpan: '',
          reportType: '',
          startDate: '',
          endDate: '',
          measures: [],
          splitBys: [],
          createUser: '',
        },
      ];
      const newReportConfigs = await getAllReportConfigs(selectedFranchise?.id);
      setReportConfigOptions([...noneConfig, ...newReportConfigs]);

      const noneScheduleConfig: ReportScheduleConfig[] = [
        {
          displayName: '-- Create New Schedule --',
          reportId: '',
          reportScheduleId: '',
          createUser: '',
          executionMode: 'MANUAL',
          taskFrequency: '',
          taskFirstTime: '',
          lastExecutionTime: '',
          active: false,
          emails: [],
          lastUpdated: '',
        },
      ];
      const newReportScheduleConfigs = await getAllReportScheduleConfigs(
        selectedFranchise?.id
      );
      setReportScheduleConfigOptions([
        ...noneScheduleConfig,
        ...newReportScheduleConfigs,
      ]);
    }
  };

  const fetchFranchiseUsers = async (): Promise<void> => {
    await getFranchiseUsers(selectedFranchise?.id ?? '')
      .then((data) => {
        // Non-admins get a 403 from /franchises/:id/users and getFranchiseUsers
        // swallows it as an empty list. Fall back to the signed-in user so they
        // can still schedule a report to themselves.
        const email = user?.attributes?.email ?? '';
        const fallback: Array<{ userId: string; email: string }> =
          data.userData.length === 0 && email !== ''
            ? [{ userId: user?.attributes?.sub ?? email, email }]
            : [];
        const users = [...data.userData, ...fallback].sort((a, b) =>
          a.email.localeCompare(b.email)
        );
        setFranchiseUsers(users);
        setDisplayedUsers(users);
      })
      .catch((error) => {
        console.error(
          `Error fetching users for franchise ${selectedFranchise?.id}:`,
          error
        );
      });
  };

  useEffect(() => {
    setSelectedReportScheduleConfigId('');
    handleSelectedReportScheduleConfigChange('');
    void fetchFranchiseUsers();
    void fetchReportAndScheduleConfigs();
    setDisplayedUsers([]);
    // clear all the selected values when the franchise changes
  }, [selectedFranchise]);

  useEffect(() => {
    if (selectedReportScheduleConfigId !== '') {
      handleSelectedReportScheduleConfigChange(selectedReportScheduleConfigId);
    }
  }, [reportConfigOptions, reportScheduleConfigOptions]);

  const naturalSortOptions = (
    options: Array<{ id: string; value: string }>
  ) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return [...options].sort((a, b) => collator.compare(a.value, b.value));
  };

  async function handleSaveReportScheduleConfig(): Promise<void> {
    const franchiseId = selectedFranchise?.id ?? '';
    if (
      franchiseId === '' ||
      selectedDates.length === 0
    ) {
      console.error('Missing required data to generate the report.');
      return;
    }

    const formatDate = (date: Date): string => {
      // convert date from current time zone to UTC:
      const curDate = new Date(date);

      curDate.setHours(parseInt(selectedTime.split(':')[0]));
      curDate.setMinutes(parseInt(selectedTime.split(':')[1]));

      const utcTimeMs = curDate.getTime() + (curDate.getTimezoneOffset() * 60000);
      const utcDate = new Date(utcTimeMs);

      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');

      const newHours = utcDate.getHours()
      const newMinutes = utcDate.getMinutes()
      const newTime = newHours.toString().padStart(2, '0') + ':' + newMinutes.toString().padStart(2, '0');

      
      return `${year}-${month}-${day} ${newTime}:00`;
    };
    const formatFrequency = reportFrequency + selectedFrequency.value;

    if (selectedReportScheduleConfig.reportScheduleId !== '') {
      await updateReportScheduleConfig(
        franchiseId,
        selectedReportScheduleConfigName,
        selectedReportScheduleConfig.reportScheduleId,
        selectedReportConfig.reportId,
        'RECURRENT',
        formatFrequency,
        formatDate(selectedDates[0]),
        selectedFranchiseUsers.map((user) => user.value),
        true
      )
        .then(() => toast('Successfully updated report config.'))
        .then(async () => {
          await fetchReportConfigs();
        });
    } else {
      await saveNewReportScheduleConfig(
        franchiseId,
        selectedReportScheduleConfigName,
        selectedReportConfig.reportId,
        'RECURRENT',
        formatFrequency,
        formatDate(selectedDates[0]),
        selectedFranchiseUsers.map((user) => user.value)
      )
        .then((newReport) => {
          if (
            newReport !== undefined &&
            newReport !== null &&
            typeof newReport !== 'boolean' &&
            newReport.reportScheduleId !== ''
          ) {
            setSelectedReportScheduleConfigId(newReport.reportScheduleId);
          }
        })
        .then(async () => {
          await fetchReportAndScheduleConfigs();
        });
    }
  }
  async function handleDeleteReportSchedule(): Promise<void> {
    const franchiseId = selectedFranchise?.id ?? '';
    if (
      franchiseId === '' ||
      selectedDates.length === 0 ||
      selectedReportConfig.reportId === '' ||
      selectedReportConfig.reportId === undefined ||
      selectedReportConfig.reportId === null
    ) {
      console.error('Missing required data to delete the report config.');
      return;
    }

    // TODO validate that the report config is not in use by a schedule before deleting

    await deleteReportScheduleConfig(selectedReportScheduleConfig.reportScheduleId, franchiseId)
      .then(() => toast('Successfully deleted report scheduleconfig.'))
      .then(async () => {
        await fetchReportAndScheduleConfigs();
        handleSelectedReportScheduleConfigChange('');
      });
  }

  function handleDateChange(dates: Date[]): void {
    handleSelectedDates(dates);
  }

  function handleTimeChange(time: string): void {
    setSelectedTime(time);
  }

  const handleSelectedFrequencyChange = (id: string) => {
    const matched = frequencies.find((freq) => freq.value === id);
    if (matched != null) {
      setSelectedFrequency(matched);
    }
  };
  const handleSelectedReportScheduleConfigChange = (id: string) => {
    const matched = reportScheduleConfigOptions.find(
      (rt) => rt.reportScheduleId === id
    );
    // change all the dropdowns based off of the selectedReportConfig
    setSelectedReportScheduleConfigId(id);

    if (matched !== undefined) {
      // report Name
      setselectedReportScheduleConfigName(
        matched.displayName !== '-- Create New Schedule --'
          ? matched.displayName
          : ''
      );

      // date + time selector
      if (
        matched.taskFirstTime !== undefined &&
        matched.taskFirstTime !== null &&
        matched.taskFirstTime !== ''
      ) {
        // task first time is in format 2025-06-02 20:00:00
        const [datePart, timePart] = matched.taskFirstTime.split(' ');
        // const newDates = new Date(datePart);
        const dateParts = '2025-06-02'.split('-');
        const newDates = new Date(
          Number(dateParts[0]), // year
          Number(dateParts[1]) - 1, // month (0-indexed)
          Number(dateParts[2]) // day
        );
        handleSelectedDates([newDates]);
        setSelectedTime(timePart);
      } else {
        setSelectedTime('00:00');
      }

      // report frequency
      if (
        matched.taskFrequency !== undefined &&
        matched.taskFrequency !== null &&
        matched.taskFrequency !== ''
      ) {
        const frequencyValue = matched.taskFrequency.slice(
          0,
          matched.taskFrequency.length - 1
        );

        setReportFrequency(frequencyValue);
      } else {
        setReportFrequency('');
      }
      const frequencyType = matched.taskFrequency.slice(
          -1,
          matched.taskFrequency.length
        );
      setSelectedFrequency({
          label: frequencies.find((f) => f.value === frequencyType)?.label ?? '-',
          value: frequencyType,
        });

      // report config
      if (
        matched.reportId !== undefined &&
        matched.reportId !== null &&
        matched.reportId !== ''
      ) {
        handleSelectedReportConfigChange(matched.reportId);
      } else {
        handleSelectedReportConfigChange('');
      }

      // franchise users
      const matchedUsers = franchiseUsers.filter((user) =>
        matched.emails.includes(user.email)
      );
      setSelectedFranchiseUsers(
        matchedUsers.map((user) => ({ id: user.userId, value: user.email }))
      );
      setSelecteReportScheduleConfig(matched);
    }
  };

  const handleSelectedFranchiseUsersChange = (id: string) => {
    const matched = franchiseUsers.find((user) => user.userId === id);
    if (matched != null) {
      setSelectedFranchiseUsers((prevSelected) =>
        prevSelected.some((user) => user.id === matched.userId)
          ? prevSelected.filter((user) => user.id !== matched.userId)
          : [...prevSelected, { id: matched.userId, value: matched.email }]
      );
    }
  };

  function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
    event.preventDefault();
    const val = event.target.value.toLowerCase();

    const filteredUsers = franchiseUsers.filter((user) => {
      if (user.email.toLowerCase().includes(val)) {
        return true;
      }

      if (user.email.toLowerCase().includes(val)) {
        return true;
      }

      return false;
    });

    setDisplayedUsers(filteredUsers);
  }

  const handleSelectedReportConfigChange = (id: string) => {
    const matched = reportConfigOptions.find((rt) => rt.reportId === id);
    // change all the dropdowns based off of the selectedReportConfig
    setSelectedReportConfigId(id);

    if (matched !== undefined) {
      setSelectedReportConfig(matched);
    }
  };

  return (
    <>
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 space-y-6 border border-slate-200 dark:border-slate-700">
          <Toaster />
          <div data-cy="analytics" className="space-y-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">
              Report Schedule
            </h1>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Existing Schedule
                  </label>
                  <DropdownFull
                    options={reportScheduleConfigOptions.map(
                      (reportScheduleConfig: ReportScheduleConfig) => ({
                        id: reportScheduleConfig.reportScheduleId,
                        value: reportScheduleConfig.displayName,
                      })
                    )}
                    selected={{
                      id: selectedReportScheduleConfig.reportScheduleId,
                      value: selectedReportScheduleConfig.displayName,
                    }}
                    handleChange={handleSelectedReportScheduleConfigChange}
                    dataCy="dropdown-existing-config"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Schedule Name
                  </label>
                  <input
                    type="text"
                    value={selectedReportScheduleConfigName}
                    className="form-input bg-white dark:bg-slate-800 w-full"
                    onChange={(e) => {
                      setselectedReportScheduleConfigName(e.target.value);
                    }}
                    data-cy="config-name-input"
                  />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Recipients
                  </label>

                  <DropdownFilter
                    title={`${selectedFranchiseUsers.length} Recipients`}
                    options={naturalSortOptions(
                      displayedUsers.map((user) => ({
                        id: (user as FranchiseUser).userId,
                        value: (user as FranchiseUser).email,
                      }))
                    )}
                    filterFunction={handleFilter}
                    selected={selectedFranchiseUsers.map((user) => ({
                      id: user.id,
                      value: user.value,
                    }))}
                    handleChange={handleSelectedFranchiseUsersChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Report To Send
                  </label>
                  <DropdownFull
                    options={reportConfigOptions.map(
                      (reportConfig: ReportConfig) => ({
                        id: reportConfig.reportId,
                        value: reportConfig.displayName,
                      })
                    )}
                    selected={{
                      id: selectedReportConfig.reportId,
                      value: selectedReportConfig.displayName,
                    }}
                    handleChange={handleSelectedReportConfigChange}
                    dataCy="dropdown-existing-config"
                  />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Report Frequency
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={reportFrequency}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        // validate the input to be a number between 1 and 30
                        if (value < 1 || value > 30) {
                          toast.error(
                            'Please enter a number between 1 and 30.'
                          );
                          return;
                        }
                        if (!isNaN(value)) {
                          setReportFrequency(value.toString());
                        }
                      }}
                      className="form-input bg-white dark:bg-slate-800 w-20"
                    />
                    <DropdownFull
                      options={frequencies.map((frequency) => ({
                        id: frequency.value,
                        value: frequency.label,
                      }))}
                      selected={{
                        id: selectedFrequency.value,
                        value: selectedFrequency.label,
                      }}
                      handleChange={handleSelectedFrequencyChange}
                      dataCy="dropdown-frequency-type"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Scheduled Start Date
                  </label>
                  <div className="flex space-x-4">
                    <Datepicker
                      key={selectedReportScheduleConfigId}
                      initDates={selectedDates}
                      handleDateChange={handleDateChange}
                      mode="single"
                      maximumDate={true}
                      disabled={selectedReportScheduleConfig.reportScheduleId !== '' }
                    />
                    <TimeSelect
                      onChange={handleTimeChange}
                      dataCy="day-part-start-time-input"
                      value={selectedTime}
                      disabled={selectedReportScheduleConfig.reportScheduleId !== '' }
                    ></TimeSelect>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              {selectedReportScheduleConfig.reportScheduleId === '' ? (
                <button
                  className="btn bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={
                    selectedReportScheduleConfigName === '' ||
                    selectedReportConfig.reportId === '' ||
                    selectedReportScheduleConfig.reportScheduleId !== '' ||
                    selectedFrequency.value === '' ||
                    reportFrequency === '' ||
                    selectedFranchiseUsers.length === 0
                  }
                  onClick={() => void handleSaveReportScheduleConfig()}
                >
                  Save Schedule
                </button>
              ) : (
                <button
                  className="btn bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={
                    selectedReportScheduleConfigName === '' ||
                    selectedReportScheduleConfig.reportScheduleId === '' ||
                    selectedReportConfig.reportId === '' ||
                    selectedFrequency.value === '' ||
                    reportFrequency === '' ||
                    selectedFranchiseUsers.length === 0 ||
                    selectedDates.length === 0
                  }
                  onClick={() => void handleSaveReportScheduleConfig()}
                >
                  Update Schedule
                </button>
              )}
              <button
                className="btn bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={selectedReportScheduleConfig.reportScheduleId === ''}
                onClick={() => {
                  void (async () => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this configuration?'
                      )
                    ) {
                      await handleDeleteReportSchedule();
                    }
                  })();
                }}
              >
                Delete Schedule
              </button>
            </div>
          </div>
        </div>
    </>
  );
}
